import * as THREE from "three";
import type { DrawnChimney } from "../chimneyInput";
import type { InteriorPanelDirection } from "../../domain/finishes/types";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import type {
	GlazingZone,
	WallOpening,
} from "../../domain/framing/generateFramedWall";
import type { HeaterRef } from "../../domain/geometry/buildingDimensions";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import type { InteriorWall } from "../../domain/interior";
import {
	createRectRoofGeometry,
	type RectRoofGeometry,
	type RectWallName,
} from "../../domain/roof/rectRoofGeometry";
import {
	ENGINE_INTERIOR_LINING_D_M as INTERIOR_LINING_D,
	ENGINE_JOIST_H_M as JOIST_H,
	ENGINE_JOIST_W_M as JOIST_W,
	ENGINE_PANEL_T_M as PANEL_T,
	ENGINE_STUD_D_M as STUD_D,
	ENGINE_STUD_W_M as STUD_W,
} from "../constants";
import type { VentFacing } from "../detailHelpers";
import { addBox } from "../geometry";
import type { BenchRenderItem } from "../interiorRenderPlan";
import type { DeckConfig, RoofConfig } from "../types";

export type SquareBaseWall = "front" | "back" | "left" | "right";

export interface SquareBaseBuildContext {
	W: number;
	L: number;
	H: number;
	roofConfig: RoofConfig;
	roofGeometry: RectRoofGeometry;
	pitchDeg: number;
	pitchRad: number;
	roofRise: number;
	hFront: number;
	hBack: number;
	hLeft: number;
	hRight: number;
	ridgeHeight: number;
	wallBase: number;
	roofBaseFront: number;
	roofBaseBack: number;
	roofMidY: number;
	wallHMm: number;
	fW: number;
	fH: number;
	rH: number;
	sW: number;
	sD: number;
	/**
	 * Interior lining build-out inward from the stud's inner face — bastufolie
	 * + liggande glespanel — before the bastupanel starts. Every room dimension
	 * (`innerW`/`innerL`, bench widths, heater clearances, ceiling extents,
	 * opening reveals) must measure through `sD + iL + pT`, not `sD + pT`.
	 */
	iL: number;
	pT: number;
}

export interface SquareBaseContextOptions {
	roofConfig: RoofConfig;
	roofJoistDepthM?: number;
	/** `BuildInput.wallHeightM`; absent = the rule default (issue 155). */
	wallHeightM?: number;
}

export interface WallFramingResult {
	wallOpenings: WallOpening[];
	surfaceOpenings: WallOpening[];
	studAxisPositions: number[];
	/**
	 * Horizontal framing members that occupy part of a stud bay, in metres from
	 * the wall base. The insulation batts derive their vertical extent from
	 * these so a batt never interpenetrates a header or an opening sill in the
	 * framing/section views (the only views the batts are ever seen in).
	 */
	horizontalMembers: Array<{
		axisStart: number;
		axisEnd: number;
		yStart: number;
		yEnd: number;
	}>;
}

export interface EntryStepPlacement {
	wall: "front" | "left" | "right";
	centerM: number;
}

export interface WallPlacementInput {
	axisCenterM: number;
	axisSizeM: number;
	yCenterM: number;
	ySizeM: number;
	normalDepthM: number;
}

export interface FoundationBuildOptions {
	namePrefix: string;
	includeEntrySteps?: boolean;
	entryCenterXM?: number;
	entryPlacement?: EntryStepPlacement;
	siteConditions?: SiteConditions;
	deckConfig?: DeckConfig | null;
}

export interface FloorBuildOptions {
	namePrefix: string;
}

export interface WallFramingBuildOptions {
	namePrefix: string;
	glazingZonesByWall?: Partial<Record<SquareBaseWall, GlazingZone[]>>;
}

export interface PanelBuildOptions {
	/** Disable when a separately rendered leaf owns door glazing. */
	renderDoorGlass?: boolean;
	namePrefix: string;
	mode: "striped" | "shell";
	/**
	 * Which way the interior bastupanel runs (issue 134). Selects the material
	 * — the lying variant carries the 90°-rotated texture — not the geometry:
	 * the panel is one textured slab per wall segment either way. Defaults to
	 * `"standing"`, the pre-134 look.
	 */
	panelDirection?: InteriorPanelDirection;
}

export interface ExteriorFacadeBuildOptions {
	namePrefix: string;
	mode: "ventilated" | "cladding-only";
	renderOpeningTrim?: boolean;
	includeCornerTrim?: boolean;
	includeBaseTrim?: boolean;
	/**
	 * Walls whose base trim (nederkantsavslut) must be skipped because an
	 * attached deck runs outward through the same slab. The two corner boards
	 * adjacent to a suppressed wall also drop their bottom extension so they
	 * don't dangle into the deck. Only honoured in the ventilated path.
	 */
	baseTrimSuppressedWalls?: SquareBaseWall[];
}

export interface RoofBuildOptions {
	namePrefix: string;
	/**
	 * `"ceiling"` (issue 156): an indoor sauna's flat ceiling — the joists
	 * alone, cut to the wall line, with no overhang, decking, covering or
	 * fascia. The room inside an existing building has no roof of its own.
	 */
	style: "sloped-detailed" | "flat" | "ceiling";
	/**
	 * Plan position of a wood stove's flue, or null without one. Joists it
	 * would pass within 100 mm of are cut and carried on a skorstensväxling
	 * (`planRoofJoistTrim`) — the same plan the BOM's trimmer row bills.
	 */
	flue?: FlueAxisM | null;
}

/** World plan position (metres) of the flue axis. */
export interface FlueAxisM {
	x: number;
	z: number;
}

export interface SaunaCoreBuildOptions {
	namePrefix: string;
	/**
	 * Which way the interior bastupanel runs (issue 134). Korsläkt: the
	 * glespanel under it has to CROSS it, so standing panel gets lying rows and
	 * lying panel gets standing runs — the same flip the BOM bills
	 * (`calculateInteriorLiningQuantities`). Defaults to `"standing"`.
	 */
	panelDirection?: InteriorPanelDirection;
	framingResults: Map<SquareBaseWall, WallFramingResult>;
	/**
	 * The lavar to draw, resolved from `InteriorLayout` (issue 131). It replaced
	 * a fixed `benchUpper`/`benchLower` pair of world positions: the engine used
	 * to decide where the benches were, which meant the BOM was billing a
	 * different description of the same bench.
	 */
	benches: readonly BenchRenderItem[];
	heater: THREE.Vector3;
	/** The type, or the design's selection when a catalog model may apply. */
	heaterType: HeaterRef;
	/**
	 * The chimney modules the quote buys (`chimneyStackFor`, the same input
	 * `withChimney` reads); drawn for a wood-fired heater, ignored otherwise.
	 */
	chimneyStack: DrawnChimney;
	heaterRotationY?: number;
	frontShield: {
		group: THREE.Group;
		position: THREE.Vector3;
		/**
		 * The plate's normal. Z (the default) closes the unit off along the
		 * room's length; X stands it beside a unit backed onto a short wall.
		 */
		normal?: "x" | "z";
	};
	sideShield: THREE.Vector3;
	/**
	 * Which wall the side shield hangs on — it decides which axis the plate is
	 * thin along. Defaults to a long wall (X-normal), which is the only case the
	 * engines generate; an AUTHORED heater may stand on a short wall.
	 */
	sideShieldWall?: InteriorWall;
	/**
	 * Fresh-air intake. `position` is the point on the mounted surface and
	 * `facing` its outward normal — the plate valve grows out from there
	 * (see `addWallVent`), so the caller no longer sizes a flat cover box.
	 */
	intakeVent?: {
		group: THREE.Group;
		position: THREE.Vector3;
		facing: VentFacing;
	};
	exhaustVent?: {
		group: THREE.Group;
		position: THREE.Vector3;
		facing: VentFacing;
	};
	/**
	 * World-Z extent of the HOT room. The bastufolie is clipped to it, so a
	 * two-room relax half gets no vapour barrier — matching the BOM, which buys
	 * foil for the sauna portion only. Omitted = the whole building is the
	 * sauna (square engine).
	 */
	vaporBarrierZRange?: { min: number; max: number };
}

export function createSquareBaseBuildContext(
	W: number,
	L: number,
	options: SquareBaseContextOptions,
): SquareBaseBuildContext {
	const H = resolveWallHeightM(options.wallHeightM);
	const roofGeometry = createRectRoofGeometry({
		width: W,
		length: L,
		wallHeight: H,
		roofConfig: options.roofConfig,
	});
	const hFront = roofGeometry.getMaxWallHeight("front");
	const hBack = roofGeometry.getMaxWallHeight("back");
	const hLeft = roofGeometry.getMaxWallHeight("left");
	const hRight = roofGeometry.getMaxWallHeight("right");
	const fW = JOIST_W;
	const fH = JOIST_H;
	const rH = options.roofJoistDepthM ?? JOIST_H;
	const sW = STUD_W;
	const sD = STUD_D;
	const iL = INTERIOR_LINING_D;
	const pT = PANEL_T;
	const wallBase = fH + pT;
	const roofBaseFront = wallBase + hFront;
	const roofBaseBack = wallBase + hBack;
	const roofMidY = wallBase + roofGeometry.getRoofHeightAt(0, 0);
	const wallHMm = Math.round(Math.max(hFront, hBack, hLeft, hRight) * 1000);

	return {
		W,
		L,
		H,
		roofConfig: options.roofConfig,
		roofGeometry,
		pitchDeg: options.roofConfig.pitchDeg,
		pitchRad: roofGeometry.pitchRad,
		roofRise: roofGeometry.roofRise,
		hFront,
		hBack,
		hLeft,
		hRight,
		ridgeHeight: roofGeometry.ridgeHeight,
		wallBase,
		roofBaseFront,
		roofBaseBack,
		roofMidY,
		wallHMm,
		fW,
		fH,
		rH,
		sW,
		sD,
		iL,
		pT,
	};
}

export function wallHeightAtAxis(
	ctx: SquareBaseBuildContext,
	wall: RectWallName,
	localAxis: number,
): number {
	return ctx.roofGeometry.getWallHeightAt(wall, localAxis);
}

export function projectWallPlacement(
	ctx: SquareBaseBuildContext,
	wall: SquareBaseWall,
	input: WallPlacementInput,
) {
	const { W, L, sD } = ctx;
	if (wall === "front" || wall === "back") {
		return {
			size: new THREE.Vector3(
				input.axisSizeM,
				input.ySizeM,
				input.normalDepthM,
			),
			position: new THREE.Vector3(
				-W / 2 + input.axisCenterM,
				input.yCenterM,
				wall === "front" ? L / 2 - sD / 2 : -L / 2 + sD / 2,
			),
		};
	}

	return {
		size: new THREE.Vector3(input.normalDepthM, input.ySizeM, input.axisSizeM),
		position: new THREE.Vector3(
			wall === "left" ? -W / 2 + sD / 2 : W / 2 - sD / 2,
			input.yCenterM,
			-L / 2 + input.axisCenterM,
		),
	};
}

export function addProjectedWallBox(
	ctx: SquareBaseBuildContext,
	wall: SquareBaseWall,
	input: WallPlacementInput,
	material: THREE.Material,
	group: THREE.Group,
	name: string,
) {
	const placement = projectWallPlacement(ctx, wall, input);
	const mesh = addBox(
		placement.size.x,
		placement.size.y,
		placement.size.z,
		material,
		placement.position.x,
		placement.position.y,
		placement.position.z,
		group,
	);
	mesh.name = name;
	return mesh;
}

export function groupOpeningsByWall(openings: WallOpening[]) {
	const grouped = new Map<SquareBaseWall, WallOpening[]>();
	for (const wall of ["front", "back", "left", "right"] as const) {
		grouped.set(wall, []);
	}
	for (const opening of openings) {
		if (
			opening.wall === "front" ||
			opening.wall === "back" ||
			opening.wall === "left" ||
			opening.wall === "right"
		) {
			grouped.get(opening.wall)!.push(opening);
		}
	}
	return grouped;
}

export function getFrontDoorCenterX(
	ctx: SquareBaseBuildContext,
	openings: WallOpening[],
	fallbackX = 0,
) {
	const frontDoor = openings.find(
		(opening) => opening.wall === "front" && opening.type === "door",
	);
	if (!frontDoor) {
		return fallbackX;
	}

	return -ctx.W / 2 + frontDoor.xMm / 1000 + frontDoor.widthMm / 2000;
}
